import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type TimePeriod = '1m' | '3m' | '6m' | '1y' | 'all';

export interface NetWorthSnapshot {
  id: string;
  user_id: string;
  snapshot_date: string; // ISO date string YYYY-MM-DD
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface UpsertNetWorthSnapshotPayload {
  user_id: string;
  snapshot_date: string;
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
  currency: string;
  updated_at: string;
}

/** Returns the ISO date string (YYYY-MM-DD) for N days ago. */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Maps a TimePeriod to the earliest date to include (undefined = no filter). */
function getStartDate(timePeriod: TimePeriod): string | undefined {
  switch (timePeriod) {
    case '1m':
      return daysAgo(30);
    case '3m':
      return daysAgo(90);
    case '6m':
      return daysAgo(180);
    case '1y':
      return daysAgo(365);
    case 'all':
      return undefined;
  }
}

export function useNetWorthSnapshots(timePeriod: TimePeriod) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['net_worth_snapshots', user?.id, timePeriod],
    queryFn: async () => {
      let query = supabase
        .from('net_worth_snapshots')
        .select('*')
        .eq('user_id', user!.id)
        .order('snapshot_date', { ascending: true });

      const startDate = getStartDate(timePeriod);
      if (startDate) {
        query = query.gte('snapshot_date', startDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as NetWorthSnapshot[];
    },
    enabled: !!user,
  });
}

export function useUpsertNetWorthSnapshot() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpsertNetWorthSnapshotPayload) => {
      const { data, error } = await supabase
        .from('net_worth_snapshots')
        .upsert(payload, { onConflict: 'user_id,snapshot_date' })
        .select()
        .single();
      if (error) throw error;
      return data as NetWorthSnapshot;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['net_worth_snapshots'] }),
  });
}
